import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useAuthContext } from 'src/auth/hooks';
import { YANDEX_API } from 'src/config-global';
import { useRouter } from 'src/routes/hooks';
import { PATH_AFTER_LOGIN } from 'src/config-global';

const YandexLoginButton = ({ isLinking = false, onSuccess }) => {
    const { yandexLogin } = useAuthContext();
    const router = useRouter();

    // Generate a unique ID for this instance to avoid collisions
    const containerId = useRef(`yandex-auth-container-${Math.random().toString(36).substr(2, 9)}`).current;

    useEffect(() => {
        console.log(`YandexLoginButton [${containerId}]: Initializing...`);
        const scriptId = 'yandex-login-script';
        let script = document.getElementById(scriptId);

        const initWidget = () => {
            if (window.YaAuthSuggest) {
                console.log(`YandexLoginButton [${containerId}]: YaAuthSuggest found, initing...`);

                // Allow some time for React to ensure the container is in the DOM
                setTimeout(() => {
                    const container = document.getElementById(containerId);
                    if (!container) {
                        console.error(`YandexLoginButton [${containerId}]: Container not found in DOM!`);
                        return;
                    }

                    window.YaAuthSuggest.init(
                        {
                            client_id: YANDEX_API.clientId,
                            response_type: 'token',
                            redirect_uri: window.location.origin + '/yandex-callback.html',
                        },
                        window.location.origin,
                        {
                            view: 'button',
                            parentId: containerId,
                            buttonSize: 'm',
                            buttonView: isLinking ? 'icon' : 'main',
                            buttonTheme: 'light',
                            buttonBorderRadius: 8,
                            buttonIcon: 'show',
                        }
                    )
                        .then((result) => {
                            console.log(`YandexLoginButton [${containerId}]: Init resolved:`, result);
                            if (result.handler) {
                                console.log(`YandexLoginButton [${containerId}]: Calling handler...`);
                                return result.handler();
                            }
                            throw new Error('No handler in result');
                        })
                        .then(async (data) => {
                            console.log(`Yandex Auth Success Data [${containerId}]:`, data);
                            if (data && (data.access_token || data.token)) {
                                const token = data.access_token || data.token;
                                try {
                                    console.log(`YandexLoginButton [${containerId}]: Calling backend wrapper...`);
                                    await yandexLogin(token, isLinking);
                                    console.log(`YandexLoginButton [${containerId}]: Backend success, redirecting...`);
                                    if (isLinking && onSuccess) {
                                        onSuccess();
                                    } else if (!isLinking) {
                                        router.push(PATH_AFTER_LOGIN);
                                    }
                                } catch (error) {
                                    console.error(`YandexLoginButton [${containerId}]: Backend error:`, error);
                                }
                            } else {
                                console.warn(`YandexLoginButton [${containerId}]: Data received but no token found:`, data);
                            }
                        })
                        .catch((error) => console.log(`YandexLoginButton [${containerId}]: Error in chain:`, error));
                }, 100);
            } else {
                console.error(`YandexLoginButton [${containerId}]: YaAuthSuggest not found`);
            }
        };

        if (!script) {
            script = document.createElement('script');
            script.id = scriptId;
            script.src = 'https://yastatic.net/s3/passport-sdk/autofill/v1/sdk-suggest-with-polyfills-latest.js';
            script.async = true;
            script.onload = initWidget;
            document.head.appendChild(script);
        } else {
            // If already loaded and script tag exists
            if (window.YaAuthSuggest) {
                initWidget();
            } else {
                // Script is added but not loaded yet
                script.addEventListener('load', initWidget);
            }
        }

        return () => {
            const container = document.getElementById(containerId);
            if (container) container.innerHTML = '';
        };
    }, [yandexLogin, router, isLinking, onSuccess, containerId]);

    return (
        <Box
            id={containerId}
            sx={{
                display: 'flex',
                justifyContent: 'center',
                my: 2,
                minHeight: '40px',
                width: '100%'
            }}
        />
    );
};

export default YandexLoginButton;
